import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import {
  Container, Box, Typography, Button, AppBar, Toolbar, IconButton,
  Alert, CircularProgress, Chip, Paper, Stack,
  useTheme, useMediaQuery,
  Select, MenuItem, FormControl,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Bolt as BoltIcon,
  Description as DescriptionIcon,
  Block as BlockIcon,
  FilterAlt as FilterAltIcon,
  ShoppingCart as ShoppingCartIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

// Sub-components
import BudgetOverview from '../components/project/BudgetOverview';
import ProjectTimeline from '../components/project/ProjectTimeline';
import MaterialsListDialog from '../components/project/MaterialsListDialog';
import ProjectSettingsDialog from '../components/project/ProjectSettingsDialog';
import DisbursementChart from '../components/project/DisbursementChart';
import PendingApprovalSection from '../components/project/PendingApprovalSection';
import ActionButtons from '../components/project/ActionButtons';
import RoomAccordion from '../components/project/RoomAccordion';

// Dialogs
import DisbursementHistory from '../components/project/DisbursementHistory';
import BulkActionBar from '../components/project/BulkActionBar';
import CreateRoomDialog from '../components/project/dialogs/CreateRoomDialog';
import ItemDialog from '../components/project/dialogs/ItemDialog';
import ChangeOrderDialog from '../components/project/dialogs/ChangeOrderDialog';
import DisburseDialog from '../components/project/dialogs/DisburseDialog';
import FlagDisputeDialog from '../components/project/dialogs/FlagDisputeDialog';
import ResolveDisputeDialog from '../components/project/dialogs/ResolveDisputeDialog';
import RevisionHistoryDialog from '../components/project/dialogs/RevisionHistoryDialog';
import CommentsDialog from '../components/project/dialogs/CommentsDialog';
import AttachmentsDialog from '../components/project/dialogs/AttachmentsDialog';
import InvoicesDialog from '../components/project/dialogs/InvoicesDialog';
import CreateInvoiceDialog from '../components/project/dialogs/CreateInvoiceDialog';
import EditWorkAreaDialog from '../components/project/dialogs/EditWorkAreaDialog';
import InviteHomeownerDialog from '../components/project/dialogs/InviteHomeownerDialog';
import EditRoomDialog from '../components/project/dialogs/EditRoomDialog';
import CloseOutDialog from '../components/project/dialogs/CloseOutDialog';

import RoleSwitcherBar from '../components/RoleSwitcherBar';

const ProjectView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, effectiveRole } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ─── Data State ───────────────────────────────────────────────────────────────
  const [project, setProject] = useState(null);
  const [budget, setBudget] = useState(null);
  const [disbursements, setDisbursements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filter, setFilter] = useState(searchParams.get('filter') || 'all');

  // ─── UI State ─────────────────────────────────────────────────────────────────
  const [budgetCollapsed, setBudgetCollapsed] = useState(false);
  const [disbursementCollapsed, setDisbursementCollapsed] = useState(false);
  const [bulkActionMode, setBulkActionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  // ─── Dialog Open/Close State ──────────────────────────────────────────────────
  const [roomDialog, setRoomDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const [changeOrderDialog, setChangeOrderDialog] = useState(false);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [disburseDialog, setDisburseDialog] = useState(false);
  const [revisionDialog, setRevisionDialog] = useState(false);
  const [disputeDialog, setDisputeDialog] = useState(false);
  const [resolveDialog, setResolveDialog] = useState(false);
  const [commentsDialog, setCommentsDialog] = useState(false);
  const [attachmentsDialog, setAttachmentsDialog] = useState(false);
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [createInvoiceDialog, setCreateInvoiceDialog] = useState(false);
  const [editWorkAreaDialog, setEditWorkAreaDialog] = useState(false);
  const [editRoomDialog, setEditRoomDialog] = useState(false);
  const [closeOutDialog, setCloseOutDialog] = useState(false);
  const [closingOut, setClosingOut] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [materialsDialog, setMaterialsDialog] = useState(false);
  const [settingsDialog, setSettingsDialog] = useState(false);

  // ─── Dialog Form State ────────────────────────────────────────────────────────
  const [newRoom, setNewRoom] = useState({ name: '', description: '', type: 'room', room_ids: [], square_footage: '', ceiling_height: '' });
  const [inviteEmail, setInviteEmail] = useState('');
  const [newItem, setNewItem] = useState({ room_id: '', title: '', type: 'materials', cost: '', image_url: '', blocked_by_item_id: null });
  const [calcMode, setCalcMode] = useState('none');
  const [calcData, setCalcData] = useState({ pricePerUnit: '', totalUnits: '' });
  const [newChangeOrder, setNewChangeOrder] = useState({ parent_item_id: null, room_id: '', title: '', type: 'materials', cost: '', image_url: '' });
  const [selectedItem, setSelectedItem] = useState(null);
  const [disbursementData, setDisbursementData] = useState({ amount: '', check_number: '', notes: '' });
  const [itemRevisions, setItemRevisions] = useState([]);
  const [disputeReason, setDisputeReason] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [itemComments, setItemComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [itemAttachments, setItemAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileVisibleToHomeowner, setFileVisibleToHomeowner] = useState(true);
  const [attachmentRefreshKey, setAttachmentRefreshKey] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [newInvoice, setNewInvoice] = useState({ includeApprovedItems: true, includeDisbursements: true, taxRate: 0, dueDate: '', notes: '' });
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [selectedWorkArea, setSelectedWorkArea] = useState(null);
  const [selectedRoomIds, setSelectedRoomIds] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [editedRoomName, setEditedRoomName] = useState('');
  const [editedSquareFootage, setEditedSquareFootage] = useState('');
  const [editedCeilingHeight, setEditedCeilingHeight] = useState('');

  // ─── Data Fetching ────────────────────────────────────────────────────────────
  const fetchProject = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/projects/${id}`);
      setProject(response.data.project);
    } catch (err) {
      setError('Failed to load project');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchBudget = useCallback(async () => {
    try {
      const response = await axios.get(`/api/projects/${id}/budget`);
      setBudget(response.data.budget);
    } catch (err) {
      console.error('Error fetching budget:', err);
    }
  }, [id]);

  const fetchDisbursements = useCallback(async () => {
    try {
      const response = await axios.get(`/api/projects/${id}/disbursements`);
      setDisbursements(response.data.disbursements);
    } catch (err) {
      console.error('Error fetching disbursements:', err);
    }
  }, [id]);

  useEffect(() => {
    fetchProject();
    fetchBudget();
    fetchDisbursements();
  }, [fetchProject, fetchBudget, fetchDisbursements]);

  // ─── Room Handlers ────────────────────────────────────────────────────────────
  const handleCreateRoom = async () => {
    try {
      await axios.post('/api/rooms', {
        project_id: id,
        name: newRoom.name,
        description: newRoom.description || null,
        type: newRoom.type,
        room_ids: newRoom.room_ids,
        square_footage: newRoom.square_footage === '' ? null : newRoom.square_footage,
        ceiling_height: newRoom.ceiling_height === '' ? null : newRoom.ceiling_height,
      });
      setRoomDialog(false);
      setNewRoom({ name: '', description: '', type: 'room', room_ids: [], square_footage: '', ceiling_height: '' });
      fetchProject();
    } catch (err) {
      setError(`Failed to create ${newRoom.type === 'room' ? 'room' : 'work area'}`);
      console.error('Error:', err);
    }
  };

  const handleInviteHomeowner = async () => {
    try {
      await axios.post(`/api/projects/${id}/invite-homeowner`, { email: inviteEmail });
      setInviteDialog(false);
      setInviteEmail('');
      setError('');
      setSuccessMessage('Invitation sent successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send invitation');
      console.error('Error:', err);
    }
  };

  const handleSaveWorkArea = async () => {
    try {
      await axios.put(`/api/rooms/${selectedWorkArea.id}`, { room_ids: selectedRoomIds });
      setEditWorkAreaDialog(false);
      setSelectedWorkArea(null);
      setSelectedRoomIds([]);
      fetchProject();
    } catch (err) {
      setError('Failed to update work area');
      console.error('Error:', err);
    }
  };

  const handleEditRoom = async () => {
    if (!editedRoomName.trim()) { setError('Room name cannot be empty'); return; }
    try {
      await axios.put(`/api/rooms/${selectedRoom.id}`, {
        name: editedRoomName,
        square_footage: editedSquareFootage === '' ? null : editedSquareFootage,
        ceiling_height: editedCeilingHeight === '' ? null : editedCeilingHeight,
      });
      setEditRoomDialog(false);
      setSelectedRoom(null);
      setEditedRoomName('');
      setEditedSquareFootage('');
      setEditedCeilingHeight('');
      fetchProject();
    } catch (err) {
      setError('Failed to update room');
      console.error('Error:', err);
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (window.confirm('Are you sure you want to delete this room? All items will be deleted.')) {
      try {
        await axios.delete(`/api/rooms/${roomId}`);
        fetchProject();
        fetchBudget();
      } catch (err) {
        setError('Failed to delete room');
        console.error('Error:', err);
      }
    }
  };

  // ─── Item Handlers ────────────────────────────────────────────────────────────
  const handleCalcDataChange = (field, value) => {
    const updated = { ...calcData, [field]: value };
    setCalcData(updated);
    if (updated.pricePerUnit && updated.totalUnits) {
      const calculatedCost = parseFloat(updated.pricePerUnit) * parseFloat(updated.totalUnits);
      setNewItem(prev => ({ ...prev, cost: calculatedCost.toFixed(2) }));
    }
  };

  const handleCreateItem = async () => {
    try {
      if (selectedItem) {
        await axios.put(`/api/items/${selectedItem.id}`, { ...newItem, cost: parseFloat(newItem.cost) });
      } else {
        await axios.post('/api/items', { ...newItem, cost: parseFloat(newItem.cost) });
      }
      setItemDialog(false);
      setSelectedItem(null);
      setNewItem({ room_id: '', title: '', type: 'materials', cost: '', image_url: '', blocked_by_item_id: null });
      setCalcMode('none');
      setCalcData({ pricePerUnit: '', totalUnits: '' });
      fetchProject();
      fetchBudget();
    } catch (err) {
      setError(selectedItem ? 'Failed to update item' : 'Failed to create item');
      console.error('Error:', err);
    }
  };

  const handleOpenChangeOrder = (item, roomId) => {
    setNewChangeOrder({ parent_item_id: item.id, room_id: roomId, title: `Change Order - ${item.title}`, type: item.type, cost: '', image_url: '' });
    setChangeOrderDialog(true);
  };

  const handleCreateChangeOrder = async () => {
    try {
      await axios.post('/api/items', { ...newChangeOrder, cost: parseFloat(newChangeOrder.cost) });
      setChangeOrderDialog(false);
      setNewChangeOrder({ parent_item_id: null, room_id: '', title: '', type: 'materials', cost: '', image_url: '' });
      fetchProject();
      fetchBudget();
    } catch (err) {
      setError('Failed to create change order');
      console.error('Error:', err);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await axios.delete(`/api/items/${itemId}`);
        fetchProject();
        fetchBudget();
      } catch (err) {
        setError('Failed to delete item');
        console.error('Error:', err);
      }
    }
  };

  const handleDeleteProject = async () => {
    if (window.confirm(`Are you sure you want to delete the entire project "${project.name}"? This action cannot be undone and will delete all rooms, items, and associated data.`)) {
      try {
        await axios.delete(`/api/projects/${id}`);
        navigate('/');
      } catch (err) {
        setError('Failed to delete project');
        console.error('Error:', err);
      }
    }
  };

  // ─── Close Out Handler ────────────────────────────────────────────────────────
  const handleCloseOut = async () => {
    setClosingOut(true);
    try {
      await axios.post(`/api/projects/${id}/complete`);
      setCloseOutDialog(false);
      setError('');
      setSuccessMessage('Project marked as complete!');
      fetchProject();
      fetchBudget();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to close out project');
      setCloseOutDialog(false);
      console.error('Error:', err);
    } finally {
      setClosingOut(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === project.status) return;
    setChangingStatus(true);
    try {
      await axios.put(`/api/projects/${id}`, { status: newStatus });
      setSuccessMessage(`Status changed to ${newStatus}`);
      fetchProject();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setChangingStatus(false);
    }
  };

  // ─── Approval Handlers ────────────────────────────────────────────────────────
  const handleApproval = async (itemId, approvalType, status) => {
    try {
      const updateData = {};
      if (effectiveRole === 'contractor') updateData.contractor_approval = status;
      else if (effectiveRole === 'homeowner') updateData.homeowner_approval = status;
      else if (effectiveRole === 'admin') updateData.admin_approval = status;
      await axios.put(`/api/items/${itemId}`, updateData);
      fetchProject();
      fetchBudget();
    } catch (err) {
      setError('Failed to update approval');
      console.error('Error:', err);
    }
  };

  const handleMarkWorkComplete = async (itemId) => {
    try {
      await axios.put(`/api/items/${itemId}`, { work_completed: true });
      fetchProject();
      fetchBudget();
    } catch (err) {
      setError('Failed to mark work complete');
      console.error('Error:', err);
    }
  };

  const handleCompletionApproval = async (itemId, role, status) => {
    try {
      const updateData = {};
      if (role === 'homeowner') updateData.homeowner_completion_approval = status;
      else if (role === 'contractor') updateData.contractor_completion_approval = status;
      else if (role === 'admin') updateData.admin_completion_approval = status;
      await axios.put(`/api/items/${itemId}`, updateData);
      fetchProject();
      fetchBudget();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update completion approval');
      console.error('Error:', err);
    }
  };

  const handleItemStatusUpdate = async (itemId, newStatus) => {
    try {
      await axios.put(`/api/items/${itemId}`, { status: newStatus });
      fetchProject();
      fetchBudget();
    } catch (err) {
      setError('Failed to update item status');
      console.error('Error:', err);
    }
  };

  // ─── Disbursement Handlers ────────────────────────────────────────────────────
  const handleOpenDisburseDialog = (item) => {
    const remaining = parseFloat(item.cost) - parseFloat(item.amount_disbursed || 0);
    setSelectedItem(item);
    setDisbursementData({ amount: remaining.toFixed(2), check_number: '', notes: '' });
    setDisburseDialog(true);
  };

  const handleDisburseFunds = async () => {
    try {
      await axios.post(`/api/items/${selectedItem.id}/disburse`, {
        amount: parseFloat(disbursementData.amount),
        check_number: disbursementData.check_number || null,
        notes: disbursementData.notes || null,
      });
      setDisburseDialog(false);
      setDisbursementData({ amount: '', check_number: '', notes: '' });
      setSelectedItem(null);
      fetchProject();
      fetchBudget();
      fetchDisbursements();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disburse funds');
      console.error('Error:', err);
    }
  };

  // ─── Bulk Action Handlers ─────────────────────────────────────────────────────
  const handleToggleItem = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId) ? prev.filter(i => i !== itemId) : [...prev, itemId]
    );
  };

  const handleDeselectAll = () => setSelectedItems([]);

  const handleBulkApprove = async () => {
    try {
      await axios.post('/api/items/bulk-approve', { item_ids: selectedItems, approval_status: 'approved' });
      setSelectedItems([]);
      setBulkActionMode(false);
      fetchProject();
      fetchBudget();
    } catch (err) {
      setError('Failed to approve items');
      console.error('Error:', err);
    }
  };

  const handleBulkReject = async () => {
    try {
      await axios.post('/api/items/bulk-approve', { item_ids: selectedItems, approval_status: 'rejected' });
      setSelectedItems([]);
      setBulkActionMode(false);
      fetchProject();
      fetchBudget();
    } catch (err) {
      setError('Failed to reject items');
      console.error('Error:', err);
    }
  };

  // ─── Revision Handlers ────────────────────────────────────────────────────────
  const handleViewRevisions = async (item) => {
    try {
      const response = await axios.get(`/api/items/${item.id}/revisions`);
      setItemRevisions(response.data.revisions);
      setSelectedItem(item);
      setRevisionDialog(true);
    } catch (err) {
      setError('Failed to load revision history');
      console.error('Error:', err);
    }
  };

  // ─── Dispute Handlers ─────────────────────────────────────────────────────────
  const handleFlagDispute = async () => {
    if (!disputeReason.trim()) { setError('Please provide a reason for the dispute'); return; }
    try {
      await axios.post(`/api/items/${selectedItem.id}/flag-dispute`, { reason: disputeReason });
      setDisputeDialog(false);
      setDisputeReason('');
      setSelectedItem(null);
      fetchProject();
      fetchBudget();
    } catch (err) {
      setError('Failed to flag dispute');
      console.error('Error:', err);
    }
  };

  const handleResolveDispute = async () => {
    if (!resolutionNotes.trim()) { setError('Please provide resolution notes'); return; }
    try {
      await axios.post(`/api/items/${selectedItem.id}/resolve-dispute`, { resolution: resolutionNotes });
      setResolveDialog(false);
      setResolutionNotes('');
      setSelectedItem(null);
      fetchProject();
      fetchBudget();
    } catch (err) {
      setError('Failed to resolve dispute');
      console.error('Error:', err);
    }
  };

  // ─── Comment Handlers ─────────────────────────────────────────────────────────
  const handleViewComments = async (item) => {
    try {
      const response = await axios.get(`/api/items/${item.id}/comments`);
      setItemComments(response.data.comments);
      setSelectedItem(item);
      setCommentsDialog(true);
    } catch (err) {
      setError('Failed to load comments');
      console.error('Error:', err);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await axios.post(`/api/items/${selectedItem.id}/comments`, { comment: newComment });
      setNewComment('');
      const response = await axios.get(`/api/items/${selectedItem.id}/comments`);
      setItemComments(response.data.comments);
    } catch (err) {
      setError('Failed to add comment');
      console.error('Error:', err);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    try {
      await axios.delete(`/api/comments/${commentId}`);
      const response = await axios.get(`/api/items/${selectedItem.id}/comments`);
      setItemComments(response.data.comments);
    } catch (err) {
      setError('Failed to delete comment');
      console.error('Error:', err);
    }
  };

  // ─── Attachment Handlers ──────────────────────────────────────────────────────
  const handleViewAttachments = async (item) => {
    try {
      const response = await axios.get(`/api/items/${item.id}/attachments`);
      setItemAttachments(response.data.attachments);
      setSelectedItem(item);
      setAttachmentsDialog(true);
    } catch (err) {
      setError('Failed to load attachments');
      console.error('Error:', err);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('visible_to_homeowner', fileVisibleToHomeowner);
    setUploadingFile(true);
    try {
      await axios.post(`/api/items/${selectedItem.id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const response = await axios.get(`/api/items/${selectedItem.id}/attachments`);
      setItemAttachments(response.data.attachments);
      event.target.value = '';
      setFileVisibleToHomeowner(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload file');
      console.error('Error:', err);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    try {
      await axios.delete(`/api/attachments/${attachmentId}`);
      const response = await axios.get(`/api/items/${selectedItem.id}/attachments`);
      setItemAttachments(response.data.attachments);
    } catch (err) {
      setError('Failed to delete attachment');
      console.error('Error:', err);
    }
  };

  const handleDownloadAttachment = (attachment) => {
    window.open(`${axios.defaults.baseURL}/${attachment.file_path}`, '_blank');
  };

  // ─── Invoice Handlers ─────────────────────────────────────────────────────────
  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`/api/projects/${id}/invoices`);
      setInvoices(response.data.invoices);
    } catch (err) {
      console.error('Error fetching invoices:', err);
    }
  };

  const handleOpenInvoiceDialog = () => {
    fetchInvoices();
    setInvoiceDialog(true);
  };

  const handleCreateInvoice = async () => {
    try {
      setGeneratingInvoice(true);
      await axios.post(`/api/projects/${id}/invoices`, newInvoice);
      setCreateInvoiceDialog(false);
      setNewInvoice({ includeApprovedItems: true, includeDisbursements: true, taxRate: 0, dueDate: '', notes: '' });
      fetchInvoices();
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to create invoice');
      console.error('Error creating invoice:', err);
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`/api/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download invoice');
      console.error('Error downloading invoice:', err);
    }
  };

  const handleUpdateInvoiceStatus = async (invoiceId, status) => {
    try {
      await axios.put(`/api/invoices/${invoiceId}/status`, { status });
      fetchInvoices();
    } catch (err) {
      setError('Failed to update invoice status');
      console.error('Error updating invoice status:', err);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await axios.delete(`/api/invoices/${invoiceId}`);
      fetchInvoices();
    } catch (err) {
      setError('Failed to delete invoice');
      console.error('Error deleting invoice:', err);
    }
  };

  // ─── Loading / Not Found ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!project) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Project not found</Typography>
      </Box>
    );
  }

  const canEdit = user.role === 'admin' || (effectiveRole === 'contractor' && project.contractor_id === user.id);

  // ─── Status metadata (label, color, icon) and per-role next-step microcopy ────
  const statusMeta = (status) => {
    switch (status) {
      case 'estimate':         return { label: 'Draft Estimate',    color: '#7c3aed', icon: <DescriptionIcon fontSize="small" /> };
      case 'pending_approval': return { label: 'Pending Approval',  color: '#fb8c00', icon: <HourglassEmptyIcon fontSize="small" /> };
      case 'rejected':         return { label: 'Rejected',          color: '#ef5350', icon: <BlockIcon fontSize="small" /> };
      case 'active':           return { label: 'Active',            color: '#43a047', icon: <BoltIcon fontSize="small" /> };
      case 'completed':        return { label: 'Completed',         color: '#1976d2', icon: <CheckCircleIcon fontSize="small" /> };
      default:                 return { label: status,              color: '#757575', icon: <DescriptionIcon fontSize="small" /> };
    }
  };

  const nextStepFor = (status, role) => {
    if (status === 'estimate' && (role === 'contractor' || role === 'admin')) return 'Build out the line items, then submit for the homeowner to review.';
    if (status === 'estimate' && role === 'homeowner') return 'Your contractor is still preparing this estimate.';
    if (status === 'pending_approval' && role === 'homeowner') return 'Review each line item and approve or reject the estimate below.';
    if (status === 'pending_approval' && (role === 'contractor' || role === 'admin')) return 'Waiting on the homeowner to review and approve.';
    if (status === 'rejected' && (role === 'contractor' || role === 'admin')) return 'Address the homeowner’s feedback and resubmit when ready.';
    if (status === 'rejected' && role === 'homeowner') return 'You rejected this estimate. Your contractor will revise and resubmit.';
    if (status === 'active' && role === 'contractor') return 'Mark items complete and disburse payments as work finishes.';
    if (status === 'active' && role === 'homeowner') return 'Approve completed items and track progress on each room.';
    if (status === 'active' && role === 'admin') return 'Project is active — oversee approvals and disbursements.';
    if (status === 'completed') return 'All items are closed out. Project is complete.';
    return null;
  };

  const meta = statusMeta(project.status);
  const nextStep = nextStepFor(project.status, effectiveRole);

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* AppBar */}
      <AppBar position="static" sx={{ backgroundColor: 'white', boxShadow: 2 }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/logo.png"
              alt="Congrow Logo"
              style={{ height: '60px', marginRight: '16px', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            />
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'primary.main' }} aria-label="Go back">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ ml: 2, color: 'text.primary' }}>
              {project.name}
            </Typography>
            {(effectiveRole === 'contractor' || effectiveRole === 'admin') && !project.homeowner_id && (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={() => setInviteDialog(true)}
                sx={{ ml: 3 }}
              >
                Invite Homeowner
              </Button>
            )}
            {(effectiveRole === 'contractor' || effectiveRole === 'admin')
              && project.homeowner_id && project.homeowner_invite_status === 'pending' && (
              <Chip label="Homeowner invite pending" size="small" color="info" sx={{ ml: 3 }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {user.role === 'admin' && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={project.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={changingStatus}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {['estimate', 'pending_approval', 'rejected', 'active', 'completed'].map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {canEdit && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ShoppingCartIcon />}
                onClick={() => setMaterialsDialog(true)}
                size="small"
              >
                Materials List
              </Button>
            )}
            {canEdit && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<SettingsIcon />}
                onClick={() => setSettingsDialog(true)}
                size="small"
              >
                Settings
              </Button>
            )}
            {canEdit && project.status === 'active' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => setCloseOutDialog(true)}
                size="small"
              >
                Close Out
              </Button>
            )}
            {((effectiveRole === 'contractor' && project.contractor_id === user.id) || effectiveRole === 'admin') && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteProject}
                size="small"
              >
                Delete Project
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <RoleSwitcherBar />

      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>{successMessage}</Alert>
        )}

        {/* Status Header Bar */}
        <Paper
          elevation={1}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            borderLeft: `6px solid ${meta.color}`,
            backgroundColor: `${meta.color}0D`,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 1.5, md: 2 }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Chip
              icon={meta.icon}
              label={meta.label}
              sx={{
                backgroundColor: `${meta.color}1F`,
                color: meta.color,
                fontWeight: 700,
                fontSize: '0.85rem',
                px: 0.5,
                '& .MuiChip-icon': { color: meta.color },
              }}
            />
            {nextStep && (
              <Typography variant="body2" sx={{ flex: 1, color: 'text.primary' }}>
                <Box component="span" sx={{ fontWeight: 700, color: meta.color, mr: 0.5 }}>
                  Next:
                </Box>
                {nextStep}
              </Typography>
            )}
            {filter !== 'all' && (
              <Chip
                icon={<FilterAltIcon fontSize="small" />}
                label={`Filtered: ${filter}`}
                onDelete={() => { setFilter('all'); setSearchParams({}); }}
                size="small"
                sx={{ backgroundColor: 'rgba(25, 118, 210, 0.12)', color: 'primary.dark' }}
              />
            )}
          </Stack>
        </Paper>

        {project.status === 'completed' && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{ mb: 3 }}
          >
            <strong>Project Complete</strong> — This project has been closed out.
          </Alert>
        )}

        {(() => {
          // Budget alert — surface when current item costs have crept above
          // the approved (active-status entry) budget by 10% or more.
          if (!budget || !budget.totals) return null;
          const subtotal = parseFloat(budget.totals.total_cost || 0);
          const original = parseFloat(budget.totals.original_cost || budget.totals.approved_cost || 0);
          if (!original || subtotal <= original * 1.1) return null;
          const overBy = subtotal - original;
          const pct = Math.round(((subtotal - original) / original) * 100);
          return (
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              sx={{ mb: 3 }}
            >
              <strong>Heads up:</strong> current scope is{' '}
              <strong>${overBy.toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong>{' '}
              ({pct}%) above the originally approved estimate. Time to send a change order or
              talk to {project.homeowner ? `${project.homeowner.first_name}` : 'the homeowner'}.
            </Alert>
          );
        })()}

        {project.co_approver_id && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Co-approval enabled:</strong> items above{' '}
            ${Number(project.co_approver_threshold || 0).toLocaleString()} require a second sign-off.
          </Alert>
        )}

        <BudgetOverview
          budget={budget}
          project={project}
          filter={filter}
          collapsed={budgetCollapsed}
          onToggleCollapse={() => setBudgetCollapsed(!budgetCollapsed)}
          onFilterChange={(f) => { setFilter(f); setSearchParams(f === 'all' ? {} : { filter: f }); }}
          userRole={effectiveRole}
          onOpenInvoices={handleOpenInvoiceDialog}
        />

        <ProjectTimeline projectId={id} />

        <DisbursementChart
          project={project}
          collapsed={disbursementCollapsed}
          onToggleCollapse={() => setDisbursementCollapsed(!disbursementCollapsed)}
        />

        <ActionButtons
          canEdit={canEdit}
          project={project}
          bulkActionMode={bulkActionMode}
          onAddRoom={() => { setNewRoom({ name: '', description: '', type: 'room', room_ids: [] }); setRoomDialog(true); }}
          onAddWorkArea={() => { setNewRoom({ name: '', description: '', type: 'work_area', room_ids: [] }); setRoomDialog(true); }}
          onAddItem={() => { setSelectedItem(null); setNewItem({ room_id: '', title: '', description: '', type: 'materials', cost: '', image_url: '' }); setItemDialog(true); }}
          onToggleBulk={() => { setBulkActionMode(!bulkActionMode); setSelectedItems([]); }}
        />

        <PendingApprovalSection
          project={project}
          user={user}
          onApproveItem={(itemId) => handleItemStatusUpdate(itemId, 'active')}
          onRejectItem={(itemId) => handleItemStatusUpdate(itemId, 'rejected')}
        />

        <RoomAccordion
          project={project}
          filter={filter}
          bulkActionMode={bulkActionMode}
          selectedItems={selectedItems}
          user={user}
          canEdit={canEdit}
          onToggleItem={handleToggleItem}
          onDeleteRoom={handleDeleteRoom}
          onAddItemToRoom={(roomId) => {
            setSelectedItem(null);
            setNewItem({ room_id: roomId, title: '', description: '', type: 'materials', cost: '', image_url: '' });
            setItemDialog(true);
          }}
          onEditWorkArea={(room) => {
            setSelectedWorkArea(room);
            setSelectedRoomIds(room.linked_rooms?.map(r => r.id) || []);
            setEditWorkAreaDialog(true);
          }}
          onEditRoom={(room) => {
            setSelectedRoom(room);
            setEditedRoomName(room.name);
            setEditedSquareFootage(room.square_footage ?? '');
            setEditedCeilingHeight(room.ceiling_height ?? '');
            setEditRoomDialog(true);
          }}
          onOpenItemEdit={(item) => {
            setSelectedItem(item);
            setNewItem({ title: item.title, description: item.description, cost: item.cost, type: item.type, room_id: item.room_id, image_url: item.image_url || '', blocked_by_item_id: item.blocked_by_item_id || null });
            setItemDialog(true);
          }}
          onApproval={handleApproval}
          onMarkWorkComplete={handleMarkWorkComplete}
          onCompletionApproval={handleCompletionApproval}
          onOpenChangeOrder={handleOpenChangeOrder}
          onDeleteItem={handleDeleteItem}
          onOpenDisburse={handleOpenDisburseDialog}
          onOpenComments={handleViewComments}
          onOpenAttachments={handleViewAttachments}
          onOpenRevisions={handleViewRevisions}
          onOpenDispute={(item) => { setSelectedItem(item); setDisputeDialog(true); }}
          onOpenResolveDispute={(item) => { setSelectedItem(item); setResolveDialog(true); }}
        />

        <DisbursementHistory disbursements={disbursements} />
      </Container>

      {bulkActionMode && (
        <BulkActionBar
          selectedItems={selectedItems}
          onApprove={handleBulkApprove}
          onReject={handleBulkReject}
          onDeselect={handleDeselectAll}
        />
      )}

      {/* ─── Dialogs ─────────────────────────────────────────────────────────── */}
      <CreateRoomDialog
        open={roomDialog}
        onClose={() => setRoomDialog(false)}
        newRoom={newRoom}
        onNewRoomChange={setNewRoom}
        onSubmit={handleCreateRoom}
        projectRooms={project.rooms}
        isMobile={isMobile}
      />

      <ItemDialog
        open={itemDialog}
        onClose={() => { setItemDialog(false); setSelectedItem(null); }}
        selectedItem={selectedItem}
        newItem={newItem}
        onNewItemChange={setNewItem}
        calcMode={calcMode}
        calcData={calcData}
        onCalcModeChange={(mode) => { setCalcMode(mode); setCalcData({ pricePerUnit: '', totalUnits: '' }); setNewItem(prev => ({ ...prev, cost: '' })); }}
        onCalcDataChange={handleCalcDataChange}
        onSubmit={handleCreateItem}
        projectRooms={project.rooms}
        isMobile={isMobile}
        attachmentRefreshKey={attachmentRefreshKey}
        onAttachmentUpdate={() => setAttachmentRefreshKey(prev => prev + 1)}
        user={user}
      />

      <ChangeOrderDialog
        open={changeOrderDialog}
        onClose={() => setChangeOrderDialog(false)}
        newChangeOrder={newChangeOrder}
        onNewChangeOrderChange={setNewChangeOrder}
        onSubmit={handleCreateChangeOrder}
        isMobile={isMobile}
      />

      <DisburseDialog
        open={disburseDialog}
        onClose={() => setDisburseDialog(false)}
        selectedItem={selectedItem}
        disbursementData={disbursementData}
        onDisbursementDataChange={setDisbursementData}
        onSubmit={handleDisburseFunds}
      />

      <FlagDisputeDialog
        open={disputeDialog}
        onClose={() => { setDisputeDialog(false); setDisputeReason(''); }}
        disputeReason={disputeReason}
        onDisputeReasonChange={setDisputeReason}
        onSubmit={handleFlagDispute}
      />

      <ResolveDisputeDialog
        open={resolveDialog}
        onClose={() => { setResolveDialog(false); setResolutionNotes(''); }}
        selectedItem={selectedItem}
        resolutionNotes={resolutionNotes}
        onResolutionNotesChange={setResolutionNotes}
        onSubmit={handleResolveDispute}
      />

      <RevisionHistoryDialog
        open={revisionDialog}
        onClose={() => setRevisionDialog(false)}
        selectedItem={selectedItem}
        revisions={itemRevisions}
      />

      <CommentsDialog
        open={commentsDialog}
        onClose={() => { setCommentsDialog(false); setNewComment(''); }}
        selectedItem={selectedItem}
        comments={itemComments}
        newComment={newComment}
        onNewCommentChange={setNewComment}
        onAddComment={handleAddComment}
        onDeleteComment={handleDeleteComment}
        user={user}
      />

      <AttachmentsDialog
        open={attachmentsDialog}
        onClose={() => setAttachmentsDialog(false)}
        selectedItem={selectedItem}
        attachments={itemAttachments}
        uploadingFile={uploadingFile}
        fileVisibleToHomeowner={fileVisibleToHomeowner}
        onFileVisibleChange={setFileVisibleToHomeowner}
        onFileUpload={handleFileUpload}
        onDeleteAttachment={handleDeleteAttachment}
        onDownloadAttachment={handleDownloadAttachment}
        user={user}
      />

      <InvoicesDialog
        open={invoiceDialog}
        onClose={() => setInvoiceDialog(false)}
        invoices={invoices}
        onDownloadInvoice={handleDownloadInvoice}
        onUpdateStatus={handleUpdateInvoiceStatus}
        onDeleteInvoice={handleDeleteInvoice}
        onOpenCreateInvoice={() => setCreateInvoiceDialog(true)}
      />

      <CreateInvoiceDialog
        open={createInvoiceDialog}
        onClose={() => setCreateInvoiceDialog(false)}
        newInvoice={newInvoice}
        onNewInvoiceChange={setNewInvoice}
        onSubmit={handleCreateInvoice}
        generatingInvoice={generatingInvoice}
      />

      <EditWorkAreaDialog
        open={editWorkAreaDialog}
        onClose={() => setEditWorkAreaDialog(false)}
        projectRooms={project.rooms || []}
        selectedRoomIds={selectedRoomIds}
        onRoomIdsChange={setSelectedRoomIds}
        onSubmit={handleSaveWorkArea}
      />

      <InviteHomeownerDialog
        open={inviteDialog}
        onClose={() => setInviteDialog(false)}
        inviteEmail={inviteEmail}
        onEmailChange={setInviteEmail}
        onSubmit={handleInviteHomeowner}
      />

      <EditRoomDialog
        open={editRoomDialog}
        onClose={() => setEditRoomDialog(false)}
        selectedRoom={selectedRoom}
        editedRoomName={editedRoomName}
        onNameChange={setEditedRoomName}
        editedSquareFootage={editedSquareFootage}
        onSquareFootageChange={setEditedSquareFootage}
        editedCeilingHeight={editedCeilingHeight}
        onCeilingHeightChange={setEditedCeilingHeight}
        onSubmit={handleEditRoom}
        onOpenItemEdit={(item) => {
          setSelectedItem(item);
          setNewItem({ title: item.title, description: item.description, cost: item.cost, type: item.type, room_id: item.room_id, image_url: item.image_url || '', blocked_by_item_id: item.blocked_by_item_id || null });
          setEditRoomDialog(false);
          setItemDialog(true);
        }}
        isMobile={isMobile}
      />

      <CloseOutDialog
        open={closeOutDialog}
        onClose={() => setCloseOutDialog(false)}
        projectName={project.name}
        loading={closingOut}
        onConfirm={handleCloseOut}
      />

      <MaterialsListDialog
        open={materialsDialog}
        onClose={() => setMaterialsDialog(false)}
        projectId={id}
      />

      <ProjectSettingsDialog
        open={settingsDialog}
        onClose={() => setSettingsDialog(false)}
        project={project}
        onSaved={() => fetchProject()}
      />
    </Box>
  );
};

export default ProjectView;
